// ─────────────────────────────────────────────────────────────────────────
// exportComparisonPdf(comparison, options)
//
// Orchestration ONLY. The single entry point ReleaseComparisonView.jsx
// calls. Never computes a financial figure, never re-derives anything
// buildReleaseComparison() already produced, never fetches Variance Notes
// itself — options.notes is exactly the notes map the screen already holds.
//
// Responsibilities:
//   1. Resolve the org logo (best-effort, via options.orgId — the one piece
//      of context this layer doesn't already have) — same pattern as
//      exportCostControlPdf.js.
//   2. Wire buildComparisonPdfModel() → renderComparisonPdf() → open a
//      print-ready window, synchronously opened before any await (same
//      popup-blocker-safe pattern as exportCostControlPdf.js).
// ─────────────────────────────────────────────────────────────────────────

import { supabase } from './supabase'
import { buildComparisonPdfModel } from './buildComparisonPdfModel'
import { renderComparisonPdf } from './ComparisonPdfRenderer'

async function resolveLogoUrl(options) {
  if (options.logoUrl) return options.logoUrl
  if (!options.projectId) return null
  try {
    const { data: proj } = await supabase.from('projects').select('organization_id, name, organizations(name)').eq('id', options.projectId).single()
    if (!proj?.organization_id) return { logoUrl: null, projectName: proj?.name, orgName: proj?.organizations?.name }
    const { data: org } = await supabase.from('organizations').select('logo_url').eq('id', proj.organization_id).single()
    return { logoUrl: org?.logo_url || null, projectName: proj?.name, orgName: proj?.organizations?.name }
  } catch {
    return { logoUrl: null, projectName: null, orgName: null }
  }
}

export async function exportComparisonPdf(comparison, options = {}) {
  // Open the window synchronously (before any await) so browsers don't
  // treat it as a blocked popup.
  const win = window.open('', '_blank')

  const resolved = await resolveLogoUrl(options)

  const model = buildComparisonPdfModel(comparison, {
    ...options,
    logoUrl: resolved?.logoUrl ?? options.logoUrl ?? null,
    projectName: options.projectName || resolved?.projectName || '',
    orgName: options.orgName || resolved?.orgName || '',
  })

  const html = renderComparisonPdf(model)

  if (win) {
    win.document.write(html)
    win.document.close()
  }

  return model
}
